import { forwardRef, type InputHTMLAttributes, type ReactNode, useId } from "react";

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  labelRight?: ReactNode;
  hint?: string;
  error?: string;
}

export const FormField = forwardRef<HTMLInputElement, Props>(function FormField(
  { label, labelRight, hint, error, className = "", id, ...rest },
  ref,
) {
  const auto = useId();
  const fieldId = id ?? auto;
  const describedBy = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between">
        <label htmlFor={fieldId} className="text-[13px] font-semibold text-fh-green-dark">
          {label}
        </label>
        {labelRight}
      </div>
      <input
        ref={ref}
        id={fieldId}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        className={[
          "w-full rounded-[10px] border bg-white px-3.5 py-3 text-[15px] text-fh-ink",
          "placeholder:text-fh-gray-light",
          "focus:outline-none focus:border-fh-green focus-visible:ring-2 focus-visible:ring-fh-green/30",
          error ? "border-red-500" : "border-fh-border",
          className,
        ].join(" ")}
        {...rest}
      />
      {error ? (
        <p id={`${fieldId}-error`} className="text-[13px] text-red-600">{error}</p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="text-[13px] text-fh-gray">{hint}</p>
      ) : null}
    </div>
  );
});
